import { observer } from 'mobx-react-lite'
import { lazy, Suspense } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'

import Callback from '@src/app/Callback'
import StripeCallback from '@src/app/StripeCallback'
import ThirdPartyAuthorize from '@src/app/ThirdPartyAuthorize'
import ConnectedAppSkeleton from '@src/app/components/ConnectedAppSkeleton'
import SpinnerScreen from '@src/app/components/SpinnerScreen'
import { useAppStore } from '@src/app/context'
import { useEmitFirstRender } from '@src/app/electron'
import { Flag } from '@src/app/feature-flags'
import Login from '@src/app/login'
import NativeLogin from '@src/app/login/NativeLogin'
import ParagonOAuthCallback from '@src/app/settings/Integrations/ParagonOAuthCallback'
import ParagonProvider from '@src/app/settings/Integrations/ParagonProvider'
import { UniversalLoginRoute } from '@src/app/universal-login'

import AuthGuard from './AuthGuard'
import Redirect from './Redirect'
import RouterAnalytics from './RouterAnalytics'
import VerificationRoute from './VerificationRoute'

const Onboarding = lazy(() => import('@src/app/onboarding/Onboarding'))
const LoggedInRoot = lazy(() => import('@src/app/LoggedInRoot'))
const FirstRunInbox = lazy(
  () => import('@src/app/onboarding/FirstRunInbox/FirstRunInbox'),
)

const Router = () => {
  const app = useAppStore()

  const isUniversalLoginEnabled = app.service.authV2.isEnabled

  useEmitFirstRender()

  return (
    <>
      <RouterAnalytics />
      <Suspense fallback={<ConnectedAppSkeleton />}>
        <Routes>
          <Route path="native/login/*" element={<NativeLogin />} />
          <Route path="verification/*" element={<VerificationRoute />} />
          <Route
            path="*"
            element={
              app.isLoggedIn ? (
                <AuthGuard>
                  <Outlet />
                </AuthGuard>
              ) : isUniversalLoginEnabled ? (
                <UniversalLoginRoute />
              ) : (
                <Login />
              )
            }
          >
            <Route path="callback/stripe/*" element={<StripeCallback />} />
            <Route path="callback/:provider/*" element={<Callback />} />
            <Route path="authorize/*" element={<ThirdPartyAuthorize />} />
            <Route path="onboarding/*" element={<Onboarding />} />
            <Route
              path="activation/*"
              element={
                <Flag flag="webFirstRunInbox" fallback={<Navigate to="/" />}>
                  <FirstRunInbox />
                </Flag>
              }
            />
            <Route
              path="oauth/integrations/:integration"
              element={
                <ParagonProvider fallback={<SpinnerScreen />} isElectron={app.isElectron}>
                  <ParagonOAuthCallback />
                </ParagonProvider>
              }
            />
            <Route
              path="*"
              element={
                app.storesLoaded && app.needsOnboarding ? (
                  // eslint-disable-next-line @typescript-eslint/no-deprecated
                  <Redirect to="/onboarding" />
                ) : (
                  <LoggedInRoot />
                )
              }
            />
          </Route>
        </Routes>
      </Suspense>
    </>
  )
}

export default observer(Router)
